import React, { useState, useEffect, useRef } from 'react';
import Slider from 'react-slick';
import { compact } from 'lodash';
import { withTheme } from 'styled-components';
import { withTranslation } from 'react-i18next';
import useDraggableScroll from 'use-draggable-scroll';
import findIndex from 'lodash/findIndex';
import Menu from './Menu';
import * as Styled from './styles';
import { getLocale } from '../../../helpers/locales';
import useTranslate from '../../../hooks/useTranslate';
import { META_EVENTS } from '../../../constants/CONTROLLERS_EVENT_TYPES';
import ArrowLeft from '../../../shared/components/Icons/ArrowLeft';
import ArrowRight from '../../../shared/components/Icons/ArrowRight';
import { MenuDatepickerButton } from './MenuDatepickerButton';

const currentMenuList = null;
const MAX_DATES_IN_MENU = 10;
const MAX_MENU_ITEMS_DESKTOP = 3;

const PrevArrow = (props) => (
  <button type="button" {...props}>
    <ArrowLeft />
  </button>
);
const NextArrow = (props) => (
  <button type="button" {...props}>
    <ArrowRight />
  </button>
);

const calcMenu = ({ dates, middle }) => {
  const start = Math.sign(middle - 5) === -1 ? 0 : middle - 5;
  const end = middle + 5 > dates.length ? dates.length : middle + 5;
  const menuDates = [...dates.slice(start, middle), ...dates.slice(middle, end)];

  return menuDates;
};

const HorizontalMenu = ({ dates, theme }) => {
  const { Widget } = window;
  const { i18n } = useTranslate();
  const [locale, setLocale] = useState(undefined);
  const scrollMenuRef = useRef(null);
  const sliderRef = useRef(null);
  const { onMouseDown: onTypeFilterMouseDown } = useDraggableScroll(scrollMenuRef);
  let menuList = currentMenuList;

  useEffect(() => {
    getLocale(i18n.language).then((l) => {
      setLocale(l.default);
    });
  }, [i18n.language]);

  const goToCurrentSlide = () => {
    const { width: rootWidth } = window.Widget;
    const activeSlide = findIndex(
      menuList,
      (o: any) => o.key === Widget.metaController.selectedDate,
    );
    const calcSlidesOffset = () => {
      if (activeSlide === menuList.length - 1) {
        if (rootWidth < 910) return 0;
        if (rootWidth < 1200) return 1;
        return 2;
      }

      if (rootWidth < 1200) {
        return 0;
      }

      return 1;
    };
    sliderRef.current.slickGoTo(activeSlide - calcSlidesOffset());
  };

  const onSliderInit = () => {
    const intervalId = setInterval(() => {
      if (sliderRef && sliderRef.current) {
        clearInterval(intervalId);
        goToCurrentSlide();
      }
    }, 100);
  };

  useEffect(() => {
    if (sliderRef && sliderRef.current) {
      goToCurrentSlide();
    }
  }, [Widget.metaController.selectedDate]);

  useEffect(() => {
    Widget.metaController.addListener(META_EVENTS.SELECT_DATE, updateDate);
    return () => {
      Widget.metaController.removeListener(META_EVENTS.SELECT_DATE, updateDate);
    };
  }, []);

  if (!locale) return null;

  if (dates.length > MAX_DATES_IN_MENU && !menuList) {
    const { selectedDate } = Widget.metaController;
    const middle = findIndex(dates, (date: any) => date.date === selectedDate);
    const menuDates = calcMenu({ dates, middle });

    menuList = Menu(
      menuDates.filter((i) => !!i),
      Widget.metaController.selectedDate,
      locale,
    );
  }

  if (dates.length <= MAX_DATES_IN_MENU && !menuList) {
    menuList = Menu(dates, Widget.metaController.selectedDate, locale);
  }

  function updateDate() {
    if (dates.length > MAX_DATES_IN_MENU && !menuList) {
      const { selectedDate } = Widget.metaController;
      const middle = findIndex(dates, (date: any) => date.date === selectedDate);
      const menuDates = calcMenu({ dates, middle });

      menuList = Menu(
        menuDates.filter((i) => !!i),
        Widget.metaController.selectedDate,
        locale,
      );
    }

    if (dates.length <= MAX_DATES_IN_MENU && !menuList) {
      menuList = Menu(dates, Widget.metaController.selectedDate, locale);
    }
  }

  const selectDate = (date) => {
    if (!date) return;
    const { id } = Widget.metaController.meta.dates.find((data) => data.date === date);
    Widget.metaController.selectEvent(id);
  };

  const isCarousel = menuList.length > 2;

  const getSlidesToShowCount = () => {
    if (menuList.length <= MAX_MENU_ITEMS_DESKTOP) {
      return menuList.length;
    }

    if (Widget.settings.convertToAmPmTime || Widget.settings.withAnnouncements) {
      return 2;
    }

    return MAX_MENU_ITEMS_DESKTOP;
  };

  const sliderSettings = {
    infinite: false,
    slidesToShow: getSlidesToShowCount(),
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    onInit: onSliderInit,
    variableWidth: dates.length < 4,
    draggable: false,
    responsive: compact([
      {
        breakpoint: 1100,
        settings: {
          variableWidth: false,
          slidesToShow: 1,
        },
      },
      Widget.settings.withAnnouncements && {
        breakpoint: 1215,
        settings: {
          variableWidth: false,
          slidesToShow: 1,
        },
      },
      {
        breakpoint: 1300,
        settings: {
          variableWidth: false,
          slidesToShow: 2,
        },
      },
    ]),
  };

  const scrollToSelectedItem = (value: number) => {
    const scrollId = setInterval(() => {
      if (scrollMenuRef && scrollMenuRef.current) {
        scrollMenuRef.current.scrollLeft = value;
        clearInterval(scrollId);
      }
    }, 100);
  };

  return (
    <Styled.Container fluid={!isCarousel} noCalendarIcon={dates.length === 1}>
      {theme.isMobile || dates.length < MAX_MENU_ITEMS_DESKTOP ? (
        <Styled.ScrollMenu ref={scrollMenuRef} onMouseDown={onTypeFilterMouseDown}>
          {menuList.map((item) =>
            React.cloneElement(item, {
              selectDate,
              scrollToSelectedItem,
            }),
          )}
        </Styled.ScrollMenu>
      ) : (
        <div style={{ zIndex: 99 }}>
          <Slider {...sliderSettings} ref={sliderRef}>
            {menuList.map((item) => React.cloneElement(item, { selectDate }))}
          </Slider>
        </div>
      )}
      {dates.length > MAX_MENU_ITEMS_DESKTOP && <MenuDatepickerButton />}
    </Styled.Container>
  );
};

// @ts-ignore
export default withTheme(withTranslation()(HorizontalMenu)) as any;
